import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/**
 * Debo Bijoux : barre « 4 bijoux pour 50 € / livraison offerte » et ajout rapide des bijoux suggérés.
 * Le HTML est rendu par snippets/debo-cart-boost.liquid ; ce script ajoute au panier
 * et garde la barre à jour après chaque modification du panier.
 */

const euros = (/** @type {number} */ cents) => (cents / 100).toFixed(2).replace('.', ',') + ' €';

/**
 * @param {number} count
 * @param {number} total
 * @param {number} seuil
 * @param {number} franco
 */
function message(count, total, seuil, franco) {
  const manque = seuil - count;
  if (manque === 1) return 'Plus qu’<strong>1 bijou</strong> : tes 4 bijoux passent à 50 €.';
  if (manque > 1) return 'Plus que <strong>' + manque + ' bijoux</strong> et le lot passe à 50 €.';
  if (total >= franco) return '<strong>4 bijoux pour 50 €</strong> et la livraison offerte : c’est gagné.';
  return '<strong>4 bijoux pour 50 €, c’est bon.</strong> Plus que <strong>' + euros(franco - total) + '</strong> pour la livraison offerte.';
}

/** @param {any} cart */
function sync(cart) {
  if (!cart || !Array.isArray(cart.items)) return;
  const ids = new Set(cart.items.map((/** @type {any} */ item) => String(item.product_id)));

  document.querySelectorAll('[data-debo-boost]').forEach((node) => {
    const el = /** @type {HTMLElement} */ (node);
    const seuil = Number(el.dataset.seuil) || 4;
    const franco = Number(el.dataset.franco) || 5000;
    const pct = Math.min(100, Math.round((cart.item_count / seuil) * 100));
    const msg = el.querySelector('[data-debo-msg]');
    const fill = /** @type {HTMLElement | null} */ (el.querySelector('[data-debo-fill]'));
    if (msg) msg.innerHTML = message(cart.item_count, cart.total_price, seuil, franco);
    if (fill) fill.style.width = pct + '%';
    el.hidden = cart.item_count === 0;
  });

  document.querySelectorAll('[data-debo-sugg]').forEach((node) => {
    const el = /** @type {HTMLElement} */ (node);
    const seuil = Number(el.dataset.seuil) || 4;
    let visibles = 0;
    el.querySelectorAll('[data-product-id]').forEach((child) => {
      const li = /** @type {HTMLElement} */ (child);
      const dejaPris = ids.has(String(li.dataset.productId));
      li.hidden = dejaPris;
      if (!dejaPris) visibles += 1;
    });
    const titre = el.querySelector('[data-debo-sugt]');
    if (titre) titre.textContent = seuil - cart.item_count === 1 ? 'Choisis ton 4e bijou : le lot passe à 50 €' : 'Complète tes 4 bijoux pour 50 €';
    el.hidden = cart.item_count === 0 || cart.item_count >= seuil || visibles === 0;
  });
}

function fetchCart() {
  return fetch(Theme.routes.cart_url + '.json', {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => {
    if (!response.ok) throw new Error('Panier indisponible');
    return response.json();
  });
}

function refresh() {
  if (!document.querySelector('[data-debo-boost], [data-debo-sugg]')) return;
  fetchCart().then(sync).catch(() => {});
}

// Après chaque modification du panier (ajout, quantité, suppression), on resynchronise la barre.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;
  if (!promise || typeof promise.then !== 'function') return;
  promise.then(() => setTimeout(refresh, 450)).catch(() => {});
});

// Ajout rapide d'un bijou suggéré.
document.addEventListener('click', async (event) => {
  const cible = event.target instanceof Element ? event.target.closest('[data-debo-add]') : null;
  if (!(cible instanceof HTMLButtonElement) || cible.disabled) return;
  event.preventDefault();

  const variantId = cible.dataset.deboAdd;
  if (!variantId) return;

  const libelle = cible.textContent;
  cible.disabled = true;
  cible.textContent = 'Ajout…';

  /** @type {string[]} */
  const sections = [];
  document.querySelectorAll('cart-items-component').forEach((item) => {
    if (item instanceof HTMLElement && item.dataset.sectionId) sections.push(item.dataset.sectionId);
  });

  const deferred = CartLinesUpdateEvent.createPromise();
  cible.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: sections.join(','),
      }),
    }).then((r) => r.json());

    const cart = await fetchCart();
    const didError = Boolean(response.status);

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'debo-cart-boost',
        itemCount: 1,
        sections: didError ? undefined : response.sections,
        didError,
      },
    });

    if (didError && cible.isConnected) {
      cible.textContent = 'Indisponible';
      return;
    }
  } catch (error) {
    deferred.reject(error);
    if (cible.isConnected) {
      cible.disabled = false;
      cible.textContent = libelle;
    }
  }
});
